'use client';

import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleInfo } from '@fortawesome/pro-light-svg-icons';
import { cn } from '@shared/utils/utils';

interface InfoTooltipProps {
  text: string;
  className?: string;
  iconClassName?: string;
  tooltipClassName?: string;
  dataTestId?: string;
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'auto';
  position?:
    | 'auto'
    | 'top'
    | 'bottom'
    | 'left'
    | 'right'
    | 'bottom-left'
    | 'bottom-right'
    | 'top-left'
    | 'top-right';
}

type TooltipPosition =
  | 'top'
  | 'bottom'
  | 'left'
  | 'right'
  | 'bottom-left'
  | 'bottom-right'
  | 'top-left'
  | 'top-right';

/**
 * 🎯 InfoTooltip - Componente de tooltip informativo con posicionamiento automático
 *
 * Muestra un icono de información que al hacer hover despliega un tooltip
 * con texto personalizable. El posicionamiento se calcula automáticamente
 * basándose en el espacio disponible en pantalla.
 *
 * Características:
 * - Icono de información con hover
 * - Tooltip con texto personalizable
 * - Posicionamiento automático (top, bottom, left, right)
 * - Detección de espacio disponible
 * - Responsive y adaptable
 */
const InfoTooltip = React.memo(
  ({
    text,
    className,
    iconClassName,
    tooltipClassName,
    dataTestId,
    maxWidth = 'auto',
    position = 'auto',
  }: InfoTooltipProps) => {
    const [isVisible, setIsVisible] = useState(false);
    const [calculatedPosition, setCalculatedPosition] =
      useState<TooltipPosition>('top');
    const containerRef = useRef<HTMLDivElement>(null);
    const tooltipRef = useRef<HTMLDivElement>(null);

    // 🎯 Calcular posición del tooltip
    const calculateTooltipPosition = () => {
      if (!containerRef.current || !tooltipRef.current) return;

      // En mobile, calcular la mejor posición automáticamente
      if (isMobile()) {
        const containerRect = containerRef.current.getBoundingClientRect();
        const tooltipRect = tooltipRef.current.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const viewportWidth = window.innerWidth;

        // Calcular espacio disponible en cada dirección
        const spaceAbove = containerRect.top;
        const spaceBelow = viewportHeight - containerRect.bottom;
        const spaceLeft = containerRect.left;
        const spaceRight = viewportWidth - containerRect.right;

        // Determinar la mejor posición
        if (spaceAbove >= tooltipRect.height + 20) {
          setCalculatedPosition('top');
        } else if (spaceBelow >= tooltipRect.height + 20) {
          setCalculatedPosition('bottom');
        } else if (spaceRight >= tooltipRect.width + 20) {
          setCalculatedPosition('right');
        } else if (spaceLeft >= tooltipRect.width + 20) {
          setCalculatedPosition('left');
        } else {
          // Si no hay espacio suficiente, usar bottom como fallback
          setCalculatedPosition('bottom');
        }
      } else {
        // En desktop, usar la posición especificada
        if (position === 'auto' || position === 'top') {
          setCalculatedPosition('top');
        } else {
          setCalculatedPosition(position as TooltipPosition);
        }
      }
    };

    // 🎯 Calcular posición absoluta para el portal
    const getTooltipStyle = () => {
      if (!containerRef.current) return {};

      const containerRect = containerRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;

      // En mobile, ajustar la posición para evitar que se corte
      if (isMobile()) {
        switch (calculatedPosition) {
          case 'top':
            return {
              position: 'fixed' as const,
              top: Math.max(10, containerRect.top - 8), // Mínimo 10px del borde superior
              left: Math.min(
                Math.max(10, containerRect.left + containerRect.width / 2),
                viewportWidth - 10
              ),
              transform: 'translateX(-50%) translateY(-100%)',
            };
          case 'bottom':
            return {
              position: 'fixed' as const,
              top: Math.min(containerRect.bottom + 8, viewportHeight - 10),
              left: Math.min(
                Math.max(10, containerRect.left + containerRect.width / 2),
                viewportWidth - 10
              ),
              transform: 'translateX(-50%)',
            };
          case 'left':
            return {
              position: 'fixed' as const,
              top: Math.min(
                Math.max(10, containerRect.top + containerRect.height / 2),
                viewportHeight - 10
              ),
              left: Math.max(10, containerRect.left - 8),
              transform: 'translateX(-100%) translateY(-50%)',
            };
          case 'right':
            return {
              position: 'fixed' as const,
              top: Math.min(
                Math.max(10, containerRect.top + containerRect.height / 2),
                viewportHeight - 10
              ),
              left: Math.min(containerRect.right + 8, viewportWidth - 10),
              transform: 'translateY(-50%)',
            };
          default:
            return {
              position: 'fixed' as const,
              top: Math.max(10, containerRect.top - 8),
              left: Math.min(
                Math.max(10, containerRect.left + containerRect.width / 2),
                viewportWidth - 10
              ),
              transform: 'translateX(-50%) translateY(-100%)',
            };
        }
      } else {
        // Desktop: comportamiento original
        switch (calculatedPosition) {
          case 'top':
            return {
              position: 'fixed' as const,
              top: containerRect.top - 8,
              left: containerRect.left + containerRect.width / 2,
              transform: 'translateX(-50%) translateY(-100%)',
            };
          case 'bottom':
            return {
              position: 'fixed' as const,
              top: containerRect.bottom + 8,
              left: containerRect.left + containerRect.width / 2,
              transform: 'translateX(-50%)',
            };
          case 'left':
            return {
              position: 'fixed' as const,
              top: containerRect.top + containerRect.height / 2,
              left: containerRect.left - 8,
              transform: 'translateX(-100%) translateY(-50%)',
            };
          case 'right':
            return {
              position: 'fixed' as const,
              top: containerRect.top + containerRect.height / 2,
              left: containerRect.right + 8,
              transform: 'translateY(-50%)',
            };
          case 'bottom-left':
            return {
              position: 'fixed' as const,
              top: containerRect.bottom + 8,
              left: containerRect.left,
              transform: 'translateX(0%)',
            };
          case 'bottom-right':
            return {
              position: 'fixed' as const,
              top: containerRect.bottom + 8,
              left: containerRect.right,
              transform: 'translateX(-100%)',
            };
          case 'top-left':
            return {
              position: 'fixed' as const,
              top: containerRect.top - 8,
              left: containerRect.left,
              transform: 'translateX(0%) translateY(-100%)',
            };
          case 'top-right':
            return {
              position: 'fixed' as const,
              top: containerRect.top - 8,
              left: containerRect.right,
              transform: 'translateX(-100%) translateY(-100%)',
            };
          default:
            return {
              position: 'fixed' as const,
              top: containerRect.top - 8,
              left: containerRect.left + containerRect.width / 2,
              transform: 'translateX(-50%) translateY(-100%)',
            };
        }
      }
    };

    // 🎯 Detectar si es mobile
    const isMobile = () => {
      return window.innerWidth < 768; // Tailwind sm breakpoint
    };

    // 🎯 Manejar hover (desktop) y click (mobile)
    const handleMouseEnter = () => {
      // Solo en desktop
      if (isMobile()) return;

      // Delay de 200ms antes de mostrar el tooltip
      const timeoutId = setTimeout(() => {
        setIsVisible(true);
        // Pequeño delay para asegurar que el DOM se actualice
        setTimeout(() => {
          calculateTooltipPosition();
        }, 10);
      }, 200);

      // Guardar el timeout ID para poder cancelarlo si el mouse sale antes
      (containerRef.current as any)._tooltipTimeout = timeoutId;
    };

    const handleMouseLeave = () => {
      // Solo en desktop
      if (isMobile()) return;

      // Cancelar el timeout si existe
      if ((containerRef.current as any)?._tooltipTimeout) {
        clearTimeout((containerRef.current as any)._tooltipTimeout);
        (containerRef.current as any)._tooltipTimeout = null;
      }
      setIsVisible(false);
    };

    // 🎯 Manejar click (mobile)
    const handleClick = () => {
      // Solo en mobile
      if (!isMobile()) return;

      setIsVisible(!isVisible);
      // Pequeño delay para asegurar que el DOM se actualice
      setTimeout(() => {
        calculateTooltipPosition();
      }, 10);
    };

    // 🎯 Cerrar tooltip al hacer click fuera (mobile)
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (isMobile() && isVisible && containerRef.current) {
          if (!containerRef.current.contains(event.target as Node)) {
            setIsVisible(false);
          }
        }
      };

      if (isMobile() && isVisible) {
        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
      }
    }, [isVisible]);

    // 🎯 Recalcular posición cuando cambie el tamaño de la ventana
    useEffect(() => {
      const handleResize = () => {
        if (isVisible) {
          calculateTooltipPosition();
        }
      };

      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }, [isVisible]);

    // 🎯 Obtener clases de ancho del tooltip basado en el contenido
    const getTooltipWidthClasses = () => {
      switch (maxWidth) {
        case 'sm':
          return 'max-w-[200px]';
        case 'md':
          return 'max-w-[300px]';
        case 'lg':
          return 'max-w-[400px]';
        case 'xl':
          return 'max-w-[500px]';
        case '2xl':
          return 'max-w-[600px]';
        case 'auto':
        default: {
          // Ancho automático basado en el contenido
          // Calcula el ancho estimado basado en la longitud del texto
          const textLength = text.length;
          if (textLength <= 50) {
            return 'max-w-[150px] min-w-[120px] w-max';
          } else if (textLength <= 100) {
            return 'max-w-[200px] min-w-[150px] w-max';
          } else if (textLength <= 150) {
            return 'max-w-[250px] min-w-[200px] w-max';
          } else {
            return 'max-w-[300px] min-w-[250px] w-max';
          }
        }
      }
    };

    return (
      <div
        ref={containerRef}
        className={cn('relative inline-block', className)}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={handleClick}
        data-testid={dataTestId || 'info-tooltip'}
      >
        {/* 🎯 Icono de información */}
        <FontAwesomeIcon
          icon={faCircleInfo}
          className={cn(
            'w-3 h-3 text-icons cursor-help font-extralight light:!text-lines',
            iconClassName
          )}
          data-testid={`${dataTestId}-icon`}
        />

        {/* 🎯 Tooltip con Portal */}
        {isVisible &&
          createPortal(
            <div
              ref={tooltipRef}
              className={cn(
                'z-[99999] bg-card-bg border border-card-border rounded-md shadow-lg p-3 pointer-events-none',
                getTooltipWidthClasses(),
                tooltipClassName
              )}
              style={getTooltipStyle()}
              data-testid={`${dataTestId || 'info-tooltip'}-tooltip`}
            >
              {/* 🎯 Contenido del tooltip */}
              <div className="text-xs text-text leading-relaxed">{text}</div>
            </div>,
            document.body
          )}
      </div>
    );
  }
);

InfoTooltip.displayName = 'InfoTooltip';

export default InfoTooltip;
